import { MultiSelect, type MultiSelectProps } from "@design-system/shadcn";
import React from "react";

import { useFilters } from "../filters-provider";

import { FilterBarField } from "./filter-bar-field";

interface FilterBarSelectProps extends Omit<
  MultiSelectProps,
  | "value"
  | "onValueChange"
  | "variant"
  | "closeOnSelect"
  | "selectAllLabel"
  | "countSuffix"
  | "multiLine"
> {
  name: string;
  tooltip?: string;
}

function FilterBarSelect({
  name,
  tooltip,
  options,
  selectedOptions,
  placeholder,
  "aria-label": ariaLabel,
  ...props
}: FilterBarSelectProps) {
  const { getValue, setValue } = useFilters();

  const value = getValue(name);
  const selected =
    typeof value === "string" && value !== "" ? value : undefined;
  const selectedLabel = [...options, ...(selectedOptions ?? [])].find(
    (option) => option.value === selected
  )?.label;

  return (
    <FilterBarField name={name} tooltip={tooltip}>
      <MultiSelect
        {...props}
        options={options}
        selectedOptions={selectedOptions}
        placeholder={placeholder}
        variant="pill"
        closeOnSelect
        // The field's own name stays in the accessible name once a value is
        // chosen, so a reader always knows WHICH filter they are on.
        aria-label={
          ariaLabel ??
          (selectedLabel ? `${placeholder}: ${selectedLabel}` : placeholder)
        }
        value={selected ? [selected] : []}
        onValueChange={(next) => setValue(name, next.at(-1))}
      />
    </FilterBarField>
  );
}

export { FilterBarSelect };
export type { FilterBarSelectProps };
